import React, { useState, useRef, useEffect } from 'react';
import { db, auth } from '../firebase';
import { doc, updateDoc, arrayUnion, arrayRemove, increment, addDoc, collection, serverTimestamp, onSnapshot, query, orderBy, Timestamp, where, deleteDoc } from 'firebase/firestore';
import { getValidProfilePhoto } from '../utils/profileUtils';
import { VibeScoreResult, calculateVibeScore } from '../services/vibeScoreService';
import ChakraBar from './ChakraBar';
import respectService from '../services/respectService';
import reportingService from '../services/reportingService';
import { AnalyticsIcon, GoalIcon, WellnessIcon } from './icons';
import IntroductionModal from './IntroductionModal';

interface PostInteractionsProps {
  post: any;
  currentUserId: string;
  currentUserName: string;
  onShare: (post: any) => Promise<void>;
  showDelete?: boolean;
  onDelete?: (post: any) => Promise<void>;
  showEdit?: boolean;
  onEdit?: (post: any, newContent: string) => Promise<void>;
  currentUser?: any;
  userProfile?: any;
  shouldRemoveEmojis?: boolean;
  isAdmin?: boolean;
  onAdminDelete?: (post: any) => Promise<void>;
  canEditCta?: boolean;
  onEditCta?: (post: any) => void;
}

export const PostInteractions: React.FC<PostInteractionsProps> = ({ post, currentUserId, currentUserName, onShare, showDelete = false, onDelete, showEdit = false, onEdit, currentUser, userProfile, shouldRemoveEmojis, isAdmin = false, onAdminDelete, canEditCta = false, onEditCta }) => {
  const [vibeScore, setVibeScore] = useState<VibeScoreResult | null>(null);
  const [showVibeScoreDetails, setShowVibeScoreDetails] = useState<boolean>(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const detectLang = (): 'nl' | 'en' => {
    const raw = (userProfile as any)?.language || (userProfile as any)?.lang || (userProfile as any)?.locale || (typeof navigator !== 'undefined' ? navigator.language : 'nl');
    const code = String(raw || 'nl').slice(0, 2).toLowerCase();
    return (code === 'en') ? 'en' : 'nl';
  };

  const effectiveUserId = currentUserId || auth.currentUser?.uid || '';
  const [showCommentBox, setShowCommentBox] = useState<boolean>(true);
  const [commentText, setCommentText] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [replyingTo, setReplyingTo] = useState<{ commentId: string; userId: string; userName: string } | null>(null);
  const [inlineReplyCommentId, setInlineReplyCommentId] = useState<string | null>(null);
  const [inlineReplyText, setInlineReplyText] = useState<string>('');
  const [editingComment, setEditingComment] = useState<{ id: string; content: string } | null>(null);
  const [editCommentText, setEditCommentText] = useState<string>('');
  const [shareSubmitting, setShareSubmitting] = useState<boolean>(false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [showSocialShareModal, setShowSocialShareModal] = useState<boolean>(false);
  const [shareNote, setShareNote] = useState<string>('');
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editContent, setEditContent] = useState<string>('');
  const [editSubmitting, setEditSubmitting] = useState<boolean>(false);
  const [comments, setComments] = useState<any[]>([]);
  const [loadingComments, setLoadingComments] = useState<boolean>(false);
  const COMMENT_MAX_CHARS = 500;
  const commentTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const commentEmojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const emojiContainerRef = useRef<HTMLDivElement | null>(null);
  const [showCommentEmojiPicker, setShowCommentEmojiPicker] = useState<boolean>(false);
  const submitInFlightRef = useRef<boolean>(false);
  const currentUserAvatar = getValidProfilePhoto(currentUser || auth.currentUser, userProfile || null);
  const [connecting, setConnecting] = useState<boolean>(false);
  const [requestSent, setRequestSent] = useState<boolean>(false);
  const [showConnectModal, setShowConnectModal] = useState<boolean>(false);
  const [showIntroductionModal, setShowIntroductionModal] = useState<boolean>(false);
  const [introCandidates, setIntroCandidates] = useState<Array<{ userId: string; userName: string; userPhoto?: string }>>([]);
  const [selectedIntroCandidateId, setSelectedIntroCandidateId] = useState<string>('');
  const receiverName = String((post && post.author) || 'de ontvanger');
  const [connectStatus, setConnectStatus] = useState<'idle' | 'pending' | 'accepted' | 'declined'>('idle');
  const [existingConversationId, setExistingConversationId] = useState<string | null>(null);
  const [declinedUntil, setDeclinedUntil] = useState<Date | null>(null);

  const stripEmojis = (text: string): string => {
    if (!text) return text;
    return String(text)
      .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '')
      .replace(/[\u2600-\u27BF\uFE0F\uFE0E]/g, '');
  };

  // Profanity filter function
  const filterProfanity = (text: string): string => {
    if (!text) return text;
    
    // Basic profanity filter - can be expanded
    const profanityWords = [
      'kut', 'fuck', 'shit', 'bitch', 'asshole', 'dick', 'pussy', 'bastard',
      'klootzak', 'hoer', 'tyfus', 'kanker', 'tering', 'godverdomme', 'verdomme'
    ];
    
    let filteredText = text;
    profanityWords.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      filteredText = filteredText.replace(regex, '*'.repeat(word.length));
    });
    
    return filteredText;
  };

  const submitComment = async () => {
    const effectiveUserId = currentUserId || auth.currentUser?.uid || '';
    if (!commentText.trim() || submitting || submitInFlightRef.current || !post?.id || !effectiveUserId) return;
    submitInFlightRef.current = true;
    
    // Filter profanity before submitting
    const filteredContent = filterProfanity(commentText.trim());
    
    setSubmitting(true);
    try {
      // Build comment data with optional reply info
      const commentData: any = {
        content: filteredContent,
        userId: effectiveUserId,
        userName: currentUserName,
        userPhoto: currentUserAvatar,
        timestamp: serverTimestamp()
      };
      
      // Add reply info if replying to a specific comment
      if (replyingTo) {
        commentData.replyToCommentId = replyingTo.commentId;
        commentData.replyToUserId = replyingTo.userId;
        commentData.replyToUserName = replyingTo.userName;
      }
      
      // Save comment inside the post's subcollection for real-time updates
      await addDoc(collection(db, 'communityPosts', post.id, 'comments'), commentData);

      // Close composer immediately after successful comment write.
      setCommentText('');
      setReplyingTo(null);
      setShowCommentEmojiPicker(false);
      setShowCommentBox(false);

      // Non-critical follow-up updates: should never keep composer open.
      updateDoc(doc(db, 'communityPosts', post.id), { commentsCount: increment(1) }).catch((countErr) => {
        console.warn('commentsCount update failed (non-critical):', countErr);
      });

      // Respect system integration
      const postAuthorId = post.userId || post.authorId;
      try {
        if (replyingTo && replyingTo.userId !== effectiveUserId) {
          // Direct reply to someone → create RespectRelation with that person
          await respectService.createRespectRelation(post.id, replyingTo.userId, effectiveUserId);

          // Check if the person we're replying to has also replied to us → Mutual Respect
          const theirReplies = comments.filter(c =>
            c.replyToUserId === effectiveUserId && c.userId === replyingTo.userId
          );
          if (theirReplies.length > 0) {
            await respectService.createMutualRespectRelation(post.id, effectiveUserId, replyingTo.userId);
          }
        } else if (postAuthorId && effectiveUserId !== postAuthorId && !replyingTo) {
          // Commenting on post (not a reply) → create RespectRelation with post author
          await respectService.createRespectRelation(post.id, postAuthorId, effectiveUserId);
        } else if (postAuthorId && effectiveUserId === postAuthorId) {
          // Post author replying → check for Mutual Respect with existing responders
          const existingResponders = await respectService.getRespectsForPost(post.id);
          for (const responder of existingResponders) {
            if (responder.id !== effectiveUserId) {
              await respectService.createMutualRespectRelation(post.id, effectiveUserId, responder.id);
            }
          }
        }
      } catch (respectError) {
        console.error('Respect relation creation failed:', respectError);
        // Don't block comment submission if respect fails
      }
    } catch (e) {
      console.error('Comment submit failed:', e);
    } finally {
      setSubmitting(false);
      submitInFlightRef.current = false;
    }
  };

  const submitInlineReply = async (targetCommentId: string, targetUserId: string, targetUserName: string, text: string) => {
    const effectiveUserId = currentUserId || auth.currentUser?.uid || '';
    if (!text.trim() || submitting || submitInFlightRef.current || !post?.id || !effectiveUserId) return;
    submitInFlightRef.current = true;

    const filteredContent = filterProfanity(text.trim());
    setSubmitting(true);
    try {
      const commentData: any = {
        content: filteredContent,
        userId: effectiveUserId,
        userName: currentUserName,
        userPhoto: currentUserAvatar,
        timestamp: serverTimestamp(),
        replyToCommentId: targetCommentId,
        replyToUserId: targetUserId,
        replyToUserName: targetUserName,
      };

      await addDoc(collection(db, 'communityPosts', post.id, 'comments'), commentData);

      try {
        if (targetUserId !== effectiveUserId) {
          await respectService.createRespectRelation(post.id, targetUserId, effectiveUserId);
          const theirReplies = comments.filter(c =>
            c.replyToUserId === effectiveUserId && c.userId === targetUserId
          );
          if (theirReplies.length > 0) {
            await respectService.createMutualRespectRelation(post.id, effectiveUserId, targetUserId);
          }
        }
      } catch (respectError) {
        console.error('Respect relation creation failed:', respectError);
      }

      setInlineReplyText('');
      setInlineReplyCommentId(null);
      setShowCommentEmojiPicker(false);
      setShowCommentBox(false);

      updateDoc(doc(db, 'communityPosts', post.id), { commentsCount: increment(1) }).catch((countErr) => {
        console.warn('commentsCount update failed (non-critical):', countErr);
      });
    } catch (e) {
      console.error('Inline reply submit failed:', e);
    } finally {
      setSubmitting(false);
      submitInFlightRef.current = false;
    }
  };

  const handleShareConfirm = async () => {
    if (shareSubmitting) return;
    setShareSubmitting(true);
    try {
      const payload = { ...post, shareNote };
      await onShare(payload);
      try { await updateDoc(doc(db, 'communityPosts', post.id), { shares: increment(1) }); } catch {}
      setShowShareModal(false);
      setShareNote('');
    } catch (e) {
      console.error('Share failed:', e);
      alert('Delen mislukt. Probeer opnieuw.');
    } finally {
      setShareSubmitting(false);
    }
  };

  // Edit comment
  const startEditingComment = (comment: { id: string; content: string }) => {
    setEditingComment(comment);
    setEditCommentText(comment.content);
  };

  const saveEditedComment = async () => {
    if (!editingComment || !editCommentText.trim()) return;
    
    try {
      const filteredContent = filterProfanity(editCommentText.trim());
      await updateDoc(doc(db, 'communityPosts', post.id, 'comments', editingComment.id), {
        content: filteredContent,
        editedAt: serverTimestamp()
      });
      setEditingComment(null);
      setEditCommentText('');
    } catch (e) {
      console.error('Edit comment failed:', e);
      alert('Bewerken mislukt. Probeer opnieuw.');
    }
  };

  const cancelEditingComment = () => {
    setEditingComment(null);
    setEditCommentText('');
  };

  // Delete comment
  const deleteComment = async (commentId: string) => {
    if (!window.confirm('Weet je zeker dat je deze reactie wilt verwijderen?')) return;
    
    try {
      await deleteDoc(doc(db, 'communityPosts', post.id, 'comments', commentId));
      await updateDoc(doc(db, 'communityPosts', post.id), { commentsCount: increment(-1) });
    } catch (e) {
      console.error('Delete comment failed:', e);
      alert('Verwijderen mislukt. Probeer opnieuw.');
    }
  };

  const handleEditConfirm = async () => {
    if (editSubmitting || !editContent.trim()) return;
    setEditSubmitting(true);
    try {
      if (onEdit) {
        await onEdit(post, editContent.trim());
        setShowEditModal(false);
        setEditContent('');
      }
    } catch (e) {
      console.error('Edit failed:', e);
      alert('Bewerken mislukt. Probeer opnieuw.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const openEditModal = () => {
    setEditContent(post?.content || '');
    setShowEditModal(true);
    setMenuOpen(false);
  };


  // Automatically calculate VibeScore when post is loaded
  React.useEffect(() => {
    if (post?.content) {
      const result = calculateVibeScore(post.content);
      setVibeScore(result);
    }
  }, [post?.content]);

  // Real-time comments when the box is opened
  React.useEffect(() => {
    if (!showCommentBox || !post?.id) return;
    setLoadingComments(true);
    const qRef = query(collection(db, 'communityPosts', post.id, 'comments'), orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(qRef, (snapshot) => {
      setComments(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingComments(false);
    }, (err) => {
      console.error('Load comments failed:', err);
      setLoadingComments(false);
    });
    return unsubscribe;
  }, [showCommentBox, post?.id]);

  React.useEffect(() => {
    if (!replyingTo) {
      setIntroCandidates([]);
      setSelectedIntroCandidateId('');
      return;
    }
    const candidatesMap = new Map<string, { userId: string; userName: string; userPhoto?: string }>();
    comments.forEach((comment: any) => {
      const candidateId = String(comment?.userId || '');
      const candidateName = String(comment?.userName || '');
      if (!candidateId || !candidateName) return;
      if (candidateId === effectiveUserId) return;
      if (candidateId === replyingTo.userId) return;
      if (!candidatesMap.has(candidateId)) {
        candidatesMap.set(candidateId, {
          userId: candidateId,
          userName: candidateName,
          userPhoto: comment?.userPhoto,
        });
      }
    });
    const nextCandidates = Array.from(candidatesMap.values());
    setIntroCandidates(nextCandidates);
    setSelectedIntroCandidateId((prev) => {
      if (prev && nextCandidates.some((candidate) => candidate.userId === prev)) return prev;
      return nextCandidates[0]?.userId || '';
    });
  }, [comments, effectiveUserId, replyingTo]);

  // Watch connection status between current user and post author (both directions)
  React.useEffect(() => {
    const uidA = currentUserId || auth.currentUser?.uid || '';
    const uidB = post?.userId || '';
    if (!uidA || !uidB || uidA === uidB) {
      setConnectStatus('idle');
      setExistingConversationId(null);
      setDeclinedUntil(null);
      return;
    }
    let unsub1: any; let unsub2: any;
    const updateFrom = (docs1: any[], docs2: any[]) => {
      const all = [...docs1, ...docs2].map(d => ({ id: d.id, ...(d.data?.() || d.data) }));
      if (all.length === 0) { setConnectStatus('idle'); setExistingConversationId(null); return; }
      // Prefer accepted, else any pending-like, else declined
      const accepted = all.find(d => d.status === 'accepted');
      if (accepted) { setConnectStatus('accepted'); setExistingConversationId(accepted.conversationId || null); return; }
      const pendingLike = all.find(d => ['pending','proposed','waiting'].includes(d.status));
      if (pendingLike) { setConnectStatus('pending'); setExistingConversationId(pendingLike.conversationId || null); return; }
      const declined = all.find(d => d.status === 'declined');
      if (declined) {
        setConnectStatus('declined');
        setExistingConversationId(declined.conversationId || null);
        const untilRaw = (declined.retryAfter && (declined.retryAfter.toDate?.() || declined.retryAfter)) || null;
        setDeclinedUntil(untilRaw ? new Date(untilRaw) : null);
        return;
      }
      setConnectStatus('idle');
      setExistingConversationId(null);
    };
    let docs1: any[] = []; let docs2: any[] = [];
    try {
      const q1 = query(collection(db, 'connectionRequests'), where('senderId', '==', uidA), where('receiverId', '==', uidB));
      const q2 = query(collection(db, 'connectionRequests'), where('senderId', '==', uidB), where('receiverId', '==', uidA));
      unsub1 = onSnapshot(q1, (s) => { docs1 = s.docs; updateFrom(docs1, docs2); });
      unsub2 = onSnapshot(q2, (s) => { docs2 = s.docs; updateFrom(docs1, docs2); });
    } catch (e) {
      console.error('Connect status watch failed:', e);
    }
    return () => { try { unsub1 && unsub1(); unsub2 && unsub2(); } catch {} };
  }, [currentUserId, post?.userId]);

  const insertCommentEmoji = (emoji: string) => {
    const el = commentTextAreaRef.current;
    if (!el) {
      setCommentText(prev => (prev || '') + emoji);
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const before = commentText.slice(0, start);
    const after = commentText.slice(end);
    const next = `${before}${emoji}${after}`;
    setCommentText(next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const caret = start + emoji.length;
        el.setSelectionRange(caret, caret);
      } catch {}
    });
  };

  const handleConnect = async () => {
    try {
      if (connectStatus === 'accepted' && existingConversationId) {
        window.location.hash = `#chat?c=${existingConversationId}`;
        return;
      }
      if (connecting || requestSent || connectStatus === 'pending') return;
      const senderId = currentUserId || auth.currentUser?.uid || '';
      const receiverId = post?.userId || '';
      if (!senderId || !receiverId || senderId === receiverId) return;
      setConnecting(true);
      const expiresAt = Timestamp.fromDate(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
      const reqRef = await addDoc(collection(db, 'connectionRequests'), {
        // New standardized fields
        senderId,
        receiverId,
        // Legacy fields (backwards compatibility)
        fromUserId: senderId,
        toUserId: receiverId,
        postId: post?.id || null,
        status: 'pending',
        createdAt: serverTimestamp(),
        expiresAt
      });
      const convRef = await addDoc(collection(db, 'conversations'), {
        participants: [senderId, receiverId],
        createdAt: serverTimestamp(),
        requestId: reqRef.id,
        lastMessageAt: serverTimestamp()
      });
      // Enrich request with conversation and participant display
      const senderDisplayName = currentUserName || 'Gebruiker';
      const senderPhoto = currentUserAvatar;
      const receiverDisplayName = String(post?.author || 'Ontvanger');
      const receiverPhoto = getValidProfilePhoto(currentUser || auth.currentUser, userProfile || null);
      try {
        await updateDoc(doc(db, 'connectionRequests', reqRef.id), {
          conversationId: convRef.id,
          senderName: senderDisplayName,
          senderPhoto,
          receiverName: receiverDisplayName,
          receiverPhoto
        });
      } catch {}
      await addDoc(collection(db, 'conversations', convRef.id, 'messages'), {
        type: 'system',
        text: 'Connectieverzoek verzonden. Zodra jullie beschikbaarheid is gesynchroniseerd, volgt een voorstel.',
        at: serverTimestamp(),
        authorId: 'system'
      });
      setRequestSent(true);
      setShowConnectModal(true);
      setConnectStatus('pending');
      setExistingConversationId(convRef.id);
    } catch (e) {
      console.error('Connect failed:', e);
      alert('Connectieverzoek verzenden mislukt. Probeer opnieuw.');
    } finally {
      setConnecting(false);
    }
  };

  const connectLabel = React.useMemo(() => {
    if (connecting) return 'Versturen…';
    if (connectStatus === 'accepted') return 'Connected';
    if (requestSent || connectStatus === 'pending') return 'Verzonden';
    if (connectStatus === 'declined' && declinedUntil && declinedUntil.getTime() > Date.now()) return 'Niet beschikbaar';
    return 'Connect';
  }, [connecting, connectStatus, requestSent, declinedUntil]);

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [reportingAI, setReportingAI] = useState<boolean>(false);
  const [aiReported, setAiReported] = useState<boolean>(false);

  const handleReportAIMislabeling = async () => {
    setMenuOpen(false);
    if (aiReported || reportingAI) return;
    const uid = currentUserId || auth.currentUser?.uid || '';
    const postId = post?.id;
    const authorId = post?.userId || post?.authorId || '';
    if (!uid || !postId || uid === authorId) return;
    setReportingAI(true);
    try {
      const result = await reportingService.reportAIMislabeling(
        uid,
        currentUserName,
        authorId,
        String(post?.author || 'Onbekend'),
        postId,
        String(post?.content || '').substring(0, 500),
      );
      setAiReported(true);
      if (result.strikeIssued) {
        alert('✅ Melding verwerkt. De post is automatisch gelabeld als A.I.-content en de auteur heeft een waarschuwing ontvangen.');
      } else {
        alert(`✅ Melding ontvangen (${result.totalReports}/3). Bij voldoende unieke meldingen wordt de post automatisch gelabeld.`);
      }
    } catch {
      alert('Er ging iets mis bij het indienen van je melding. Probeer opnieuw.');
    } finally {
      setReportingAI(false);
    }
  };

  React.useEffect(() => {
    if (!menuOpen) return;
    
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current) {
        const menuButton = menuRef.current.querySelector('button');
        if (menuButton && menuButton.contains(target)) {
          return;
        }
        if (!menuRef.current.contains(target)) {
          setMenuOpen(false);
        }
      }
    };
    
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', onClick);
    }, 10);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', onClick);
    };
  }, [menuOpen]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setShowCommentEmojiPicker(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Sluit emoji-picker bij klik buiten de container
  React.useEffect(() => {
    if (!showCommentEmojiPicker) return;
    const onOutsideClick = (e: MouseEvent) => {
      if (emojiContainerRef.current && !emojiContainerRef.current.contains(e.target as Node)) {
        setShowCommentEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, [showCommentEmojiPicker]);

  const connectDisabled = (
    connecting ||
    (currentUserId === post?.userId) ||
    connectStatus === 'pending' ||
    connectStatus === 'accepted' ||
    (connectStatus === 'declined' && (!!declinedUntil && declinedUntil.getTime() > Date.now()))
  );
  const selectedIntroCandidate = introCandidates.find((candidate) => candidate.userId === selectedIntroCandidateId) || null;

  const formatRelativeShort = (value: any): string => {
    try {
      const lang = detectLang();
      const d = (value && (value.toDate?.() || value)) as Date | number | undefined;
      const t = d ? new Date(d).getTime() : Date.now();
      const diffSec = Math.max(1, Math.floor((Date.now() - t) / 1000));
      let count = diffSec; let label = 's';
      let i = 0;
      const divisors = [60, 60, 24, 7, 4.345, 12];
      const labels = ['s','m','h','d','w','mo','y'];
      while (i < divisors.length && count >= divisors[i]) {
        count = Math.floor(count / divisors[i]);
        label = labels[i+1];
        i++;
      }
      const suffix = lang === 'en' ? ' ago' : ' geleden';
      return `${count}${label}${suffix}`;
    } catch {
      return '';
    }
  };




  return (
    <div className="text-sm w-full relative overflow-visible">
      {/* one-off keyframes for floating toast and scrollbar styling */}
      <style>{`
        @keyframes sk-float-up {
          0% { opacity: 0; transform: translateY(12px) translateX(0) rotate(0deg); }
          30% { opacity: 1; }
          60% { transform: translateY(-24px) translateX(6px) rotate(-1deg); }
          100% { opacity: 0; transform: translateY(-56px) translateX(-6px) rotate(-2deg); }
        }
        .sk-float-up { animation: sk-float-up 1.2s ease-out forwards; }
        
        /* Luxe scrollbar styling */
        .emoji-picker-scroll::-webkit-scrollbar {
          height: 6px;
        }
        .emoji-picker-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        .emoji-picker-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #7A9488, #8A9E78);
          border-radius: 3px;
          transition: all 0.3s ease;
        }
        .emoji-picker-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(90deg, #8A9E78, #7A9488);
          box-shadow: 0 2px 4px rgba(112, 130, 89, 0.3);
        }
        .emoji-picker-scroll::-webkit-scrollbar-thumb:active {
          background: linear-gradient(90deg, #4F6660, #7A9488);
        }
      `}</style>
      {/* Bottom action bar: right = dropdown menu */}
      <div className="mt-1 flex items-center justify-end relative">
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🖱️ Menu button clicked, current state:', menuOpen);
              setMenuOpen(v => {
                console.log('🔄 Setting menuOpen to:', !v);
                return !v;
              });
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            aria-label="Post acties"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-600 hover:bg-gray-100"
            style={{ pointerEvents: 'auto', zIndex: 1000 }}
          >
            ⋯
          </button>
          {menuOpen && (
            <div 
              className="fixed bg-white border rounded-md shadow-lg z-[9999] py-1 w-56 max-h-[80vh] overflow-y-auto" 
              role="menu" 
              aria-label="Post acties"
              style={{
                top: menuPosition ? `${menuPosition.top}px` : (menuRef.current ? `${menuRef.current.getBoundingClientRect().bottom + 4}px` : 'auto'),
                left: menuPosition ? `${menuPosition.left}px` : (menuRef.current ? `${menuRef.current.getBoundingClientRect().right - 224}px` : 'auto'),
                right: menuPosition ? 'auto' : (menuRef.current ? 'auto' : '0.5rem'),
                maxWidth: 'min(224px, calc(100vw - 2rem))',
              }}
              onMouseDown={(e) => {
                e.stopPropagation(); // Prevent closing when clicking inside menu
              }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent closing when clicking inside menu
              }}
            >
              <div className="flex items-center justify-between px-2 py-1 border-b sticky top-0 bg-white z-10">
                <span className="text-xs text-gray-500">Acties</span>
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Sluit menu"
                  className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100"
                  title="Sluiten"
                >
                  ✕
                </button>
              </div>
              <button
                onClick={() => { setMenuOpen(false); if (!connectDisabled) handleConnect(); }}
                disabled={connectDisabled}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${connectDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}`}
              >
                <span style={{ color: '#36454F' }}>🤝</span> {connectLabel}
              </button>
              <button
                onClick={() => { setMenuOpen(false); setShowCommentBox(true); }}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                💬 Antwoorden
              </button>
              {/* Edit knop voor eigen posts */}
              {showEdit && currentUserId === post?.userId && (
                <button
                  onClick={openEditModal}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  ✏️ Bewerken
                </button>
              )}
              {canEditCta && currentUserId === post?.userId && (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onEditCta?.(post);
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-[#4F6660] hover:bg-[#EAF3F0]"
                >
                  🎯 CTA aanpassen
                </button>
              )}
              <button
                onClick={() => { setMenuOpen(false); setShowShareModal(true); }}
                disabled={shareSubmitting}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${shareSubmitting ? 'text-gray-400 cursor-wait' : 'text-gray-700'}`}
              >
                ↗️ Delen
              </button>
              {/* Social Share knop voor eigen posts */}
              {currentUserId === post?.userId && (
                <button
                  onClick={() => { setMenuOpen(false); setShowSocialShareModal(true); }}
                  className="w-full text-left px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
                >
                  📱 Social Media Delen
                </button>
              )}
              {/* A.I. mislabeling report — alleen voor posts van anderen */}
              {currentUserId !== post?.userId && (post?.aiDisclosure === 'no' || !post?.aiDisclosure) && (
                <button
                  onClick={handleReportAIMislabeling}
                  disabled={aiReported || reportingAI}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-50 ${
                    aiReported ? 'text-gray-400 cursor-default' : 'text-purple-700'
                  }`}
                  title="Meld dat deze post A.I.-content bevat die niet is vermeld"
                >
                  🤖 {aiReported ? 'A.I. gemeld' : reportingAI ? 'Melden…' : 'A.I. niet vermeld melden'}
                </button>
              )}
              {showDelete && (
                <button
                  onClick={async () => {
                    setMenuOpen(false);
                    if (!onDelete) return;
                    const ok = window.confirm('Weet je zeker dat je deze post wilt verwijderen?');
                    if (!ok) return;
                    await onDelete(post);
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  🗑️ Verwijder
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Comment box is now always open by default */}

      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg border shadow p-4 w-full max-w-md">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">Delen</div>
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                aria-label="Sluiten"
                className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            <div className="text-sm text-gray-600 mb-2">Voeg een opmerking toe (optioneel):</div>
            <textarea
              value={shareNote}
              onChange={(e) => setShareNote(e.target.value)}
              rows={3}
              placeholder="Wat wil je toevoegen?"
              className="w-full border rounded-lg p-2 text-gray-700 mb-3"
            />
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowShareModal(false)} className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200">Annuleren</button>
              <button type="button" onClick={handleShareConfirm} disabled={shareSubmitting} className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">Plaatsen</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg border shadow p-4 w-full max-w-md">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">Post bewerken</div>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                aria-label="Sluiten"
                className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            <div className="text-sm text-gray-600 mb-2">Bewerk je post:</div>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={4}
              placeholder="Wat wil je delen?"
              className="w-full border rounded-lg p-2 text-gray-700 mb-3 resize-none"
              maxLength={1000}
            />
            <div className="text-xs text-gray-500 mb-3">{editContent.length}/1000 karakters</div>
            <div className="flex items-center justify-end gap-2">
              <button 
                type="button" 
                onClick={() => setShowEditModal(false)} 
                className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200"
              >
                Annuleren
              </button>
              <button 
                type="button" 
                onClick={handleEditConfirm} 
                disabled={editSubmitting || !editContent.trim()} 
                className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {editSubmitting ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Share Modal */}
      {showSocialShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg border shadow p-4 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold">📱 Social Media Delen</div>
              <button
                type="button"
                onClick={() => setShowSocialShareModal(false)}
                aria-label="Sluiten"
                className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            
            <div className="text-sm text-gray-600 mb-4">
              Deel je post op verbonden sociale media platformen:
            </div>

            {/* Platform Selectie */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="linkedin" defaultChecked className="w-4 h-4 text-blue-600 rounded" />
                <label htmlFor="linkedin" className="flex items-center space-x-2 text-sm">
                  <span>💼</span>
                  <span>LinkedIn</span>
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="twitter" className="w-4 h-4 text-blue-600 rounded" />
                <label htmlFor="twitter" className="flex items-center space-x-2 text-sm">
                  <span>🐦</span>
                  <span>Twitter</span>
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="facebook" className="w-4 h-4 text-blue-600 rounded" />
                <label htmlFor="facebook" className="flex items-center space-x-2 text-sm">
                  <span>📘</span>
                  <span>Facebook</span>
                </label>
              </div>
            </div>

            {/* Auto-share toggle */}
            <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="text-sm font-medium">Auto-share inschakelen</div>
                <div className="text-xs text-gray-600">Toekomstige posts automatisch delen</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button 
                type="button" 
                onClick={() => setShowSocialShareModal(false)} 
                className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200"
              >
                Annuleren
              </button>
              <button 
                type="button" 
                onClick={() => {
                  // TODO: Implement social sharing logic
                  alert('Social sharing functionaliteit wordt geïmplementeerd!');
                  setShowSocialShareModal(false);
                }} 
                className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Delen
              </button>
            </div>
          </div>
        </div>
      )}

      {showCommentBox && (
        <div className="mt-3">
          <div className="mb-2">
            <span className="text-xs text-gray-500">Antwoorden</span>
          </div>
          {loadingComments ? (
            <div className="text-xs text-gray-500 mb-2">Reacties laden…</div>
          ) : comments.length > 0 ? (
            <div className="mb-4 space-y-2">
              {comments.map((c: any, index: number) => {
                const prev = comments[index - 1];
                const isStacked = !!prev && prev.userId === c.userId;
                const showAvatar = !isStacked;
                const isReply = !!c.replyToCommentId;
                const isOwn = c.userId === (currentUserId || auth.currentUser?.uid);

                const parentComment = isReply ? comments.find((p: any) => p.id === c.replyToCommentId) : null;
                const parentText = parentComment?.content || '';
                const parentTextTruncated = parentText.length > 70
                  ? parentText.slice(0, 70) + '…'
                  : parentText;
                // Toon quote alleen als de vorige comment niet al een reply was aan dezelfde parent
                const prevAlsoRepliesToSameParent = !!prev && prev.replyToCommentId === c.replyToCommentId;
                const showQuote = isReply && !prevAlsoRepliesToSameParent;

                return (
                  <div 
                    key={c.id} 
                    className="flex gap-2"
                  >
                    <div className="w-6 flex-shrink-0">
                  <img
                    src={c.userPhoto || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(c.userName || 'U') + '&background=cccccc&color=fff&size=32'}
                    alt=""
                        className={`rounded-full object-cover mt-0.5 transition-all ${showAvatar ? 'w-6 h-6 opacity-100' : 'w-4 h-4 opacity-0'}`}
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.src = 'https://ui-avatars.com/api/?name=U&background=cccccc&color=fff&size=32';
                    }}
                  />
                    </div>
                    <div className="flex-1 min-w-0">
                    {editingComment?.id === c.id ? (
                      <div className="space-y-1">
                        <textarea
                          value={editCommentText}
                          onChange={(e) => setEditCommentText(e.target.value)}
                          className="w-full p-2 border border-gray-200 rounded text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                          rows={2}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button type="button" onClick={saveEditedComment} className="text-xs text-blue-600 hover:underline">Opslaan</button>
                          <button type="button" onClick={cancelEditingComment} className="text-xs text-gray-500 hover:underline">Annuleren</button>
                        </div>
                      </div>
                    ) : (
                      <>
                          <div
                            className={`rounded-xl px-3 ${isStacked ? 'py-1.5' : 'py-2'} inline-block max-w-full ${
                              isOwn ? 'bg-blue-50 border border-blue-100' : 'bg-gray-100 border border-gray-200'
                            }`}
                          >
                            {showAvatar && (
                          <span className="font-medium text-xs" style={{ color: '#1F2933' }}>{c.userName || 'Gebruiker'}</span>
                            )}
                          {showQuote && c.replyToUserName && (
                            <div className="mt-0.5 mb-1 rounded px-2 py-1 border-l-2 border-blue-400 bg-blue-50/70 flex items-baseline gap-1 min-w-0">
                              <span className="font-semibold text-[10px] text-blue-600 whitespace-nowrap flex-shrink-0">{c.replyToUserName}:</span>
                              {parentTextTruncated && (
                                <span className="text-[10px] text-gray-400 truncate leading-tight">
                                  {shouldRemoveEmojis ? stripEmojis(parentTextTruncated) : parentTextTruncated}
                                </span>
                              )}
                            </div>
                          )}
                            <p className={`text-sm ${showAvatar && !isReply ? 'mt-0.5' : ''} break-words`} style={{ color: '#1F2933' }}>
                            {shouldRemoveEmojis ? stripEmojis(c.content || '') : (c.content || '')}
                            {c.editedAt && <span className="text-xs ml-1" style={{ color: '#5B6B75' }}>(bewerkt)</span>}
                          </p>
                        </div>
                          <div className="flex items-center gap-3 mt-0.5 ml-2 text-[11px] overflow-x-auto no-scrollbar" style={{ color: '#5B6B75' }}>
                          <span className="whitespace-nowrap flex-shrink-0">{formatRelativeShort(c.timestamp)}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setInlineReplyCommentId(prev => prev === c.id ? null : c.id);
                              setInlineReplyText('');
                            }}
                              className="whitespace-nowrap flex-shrink-0 hover:underline text-gray-400 hover:text-gray-600"
                          >
                            Antwoorden
                          </button>
                          {c.userId === (currentUserId || auth.currentUser?.uid) && (
                            <>
                              <button type="button" onClick={() => startEditingComment({ id: c.id, content: c.content })} className="whitespace-nowrap flex-shrink-0 hover:underline">Bewerken</button>
                              <button type="button" onClick={() => deleteComment(c.id)} className="whitespace-nowrap flex-shrink-0 text-red-500 hover:underline">Verwijderen</button>
                            </>
                          )}
                        </div>
                        {inlineReplyCommentId === c.id && (
                          <div className="mt-2 flex items-start gap-2">
                            <img
                              src={currentUserAvatar}
                              alt=""
                              className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-0.5"
                              onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=U&background=cccccc&color=fff&size=32'; }}
                            />
                            <div className="flex-1 min-w-0">
                              <textarea
                                autoFocus
                                value={inlineReplyText}
                                onChange={(e) => setInlineReplyText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    submitInlineReply(c.id, c.userId, c.userName || 'Gebruiker', inlineReplyText);
                                  }
                                  if (e.key === 'Escape') {
                                    setInlineReplyCommentId(null);
                                    setInlineReplyText('');
                                  }
                                }}
                                placeholder={`Antwoord aan @${c.userName || 'Gebruiker'}…`}
                                rows={2}
                                maxLength={COMMENT_MAX_CHARS}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                                style={{ color: '#1F2933' }}
                              />
                              <div className="flex gap-2 mt-1 justify-end">
                                <button
                                  type="button"
                                  onClick={() => { setInlineReplyCommentId(null); setInlineReplyText(''); }}
                                  className="text-xs text-gray-500 hover:underline"
                                >
                                  Annuleer
                                </button>
                                <button
                                  type="button"
                                  onClick={() => submitInlineReply(c.id, c.userId, c.userName || 'Gebruiker', inlineReplyText)}
                                  disabled={!inlineReplyText.trim() || submitting}
                                  className="text-xs px-3 py-1 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-40 transition-opacity"
                                >
                                  Verzenden
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="text-xs mb-2" style={{ color: '#5B6B75' }}>Nog geen reacties</div>
          )}
          {/* Replying to indicator */}
          {replyingTo && (
            <div className="mb-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-700">
                  Antwoord aan <span className="font-medium">@{replyingTo.userName}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setReplyingTo(null)}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  ✕ Annuleer
                </button>
              </div>
              {introCandidates.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border border-[#B8CEC8] bg-white text-[#3C4F49]">
                    Voorstellen aan
                  </span>
                  <select
                    value={selectedIntroCandidateId}
                    onChange={(e) => setSelectedIntroCandidateId(e.target.value)}
                    className="px-2.5 py-1.5 rounded-md border border-[#E0DDD8] text-xs text-[#1C1C1C] bg-white"
                  >
                    {introCandidates.map((candidate) => (
                      <option key={candidate.userId} value={candidate.userId}>
                        {candidate.userName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowIntroductionModal(true)}
                    disabled={!selectedIntroCandidate}
                    className="px-2.5 py-1.5 rounded-md text-xs font-semibold disabled:opacity-40"
                    style={{ backgroundColor: '#2E5F56', color: '#FFFFFF' }}
                  >
                    Open voorstel
                  </button>
                </div>
              )}
            </div>
          )}
          
          <div className="mt-4 flex items-start gap-3">
            <img
              src={currentUserAvatar}
              alt="Profiel"
              className="w-8 h-8 rounded-full object-cover"
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement;
                target.src = 'https://ui-avatars.com/api/?name=U&background=cccccc&color=fff&size=128';
              }}
            />
            <div className="flex-1 min-w-0">
              <textarea
                ref={commentTextAreaRef}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  // Enter zonder Shift = submit
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (commentText.trim() && !submitting) {
                      submitComment();
                    }
                  }
                  // Shift+Enter = nieuwe regel (default gedrag)
                  // Escape = sluit alles
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setShowCommentEmojiPicker(false);
                    setReplyingTo(null);
                    setCommentText('');
                  }
                }}
                rows={3}
                placeholder={replyingTo ? `Antwoord aan @${replyingTo.userName}...` : "Schrijf een reactie..."}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                style={{ color: '#1F2933' }}
                maxLength={COMMENT_MAX_CHARS}
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <div ref={emojiContainerRef} className="relative inline-block">
                  <button
                    ref={commentEmojiButtonRef}
                    type="button"
                    onClick={() => setShowCommentEmojiPicker(v => !v)}
                    className="px-2.5 py-1 rounded-md border border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100 text-xs font-medium transition-colors"
                  >
                    😊 Emoji
                  </button>
                  {showCommentEmojiPicker && (
                    <div
                      className="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-lg border z-50 p-2"
                      style={{ minWidth: '200px', transform: 'translateY(-4px)' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <div className="grid grid-cols-6 gap-1">
                        {['😊','😂','❤️','👍','🔥','💯','🎉','😍','🤔','👏','💪','🚀'].map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              insertCommentEmoji(emoji);
                              setShowCommentEmojiPicker(false);
                            }}
                            className="p-2 text-lg hover:bg-gray-100 rounded transition-colors w-8 h-8 flex items-center justify-center"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${commentText.length >= COMMENT_MAX_CHARS ? 'text-red-600' : 'text-gray-500'}`}>{commentText.length}/{COMMENT_MAX_CHARS}</span>
                  <button 
                    type="button" 
                    onClick={() => { 
                      setReplyingTo(null);
                      setCommentText('');
                      setShowCommentBox(false); 
                    }} 
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 text-sm font-medium transition-colors"
                  >
                    Annuleren
                  </button>
                  <button 
                    type="button" 
                    onClick={submitComment} 
                    disabled={submitting || !commentText.trim()} 
                    className="px-4 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors shadow-sm"
                  >
                    {submitting ? 'Plaatsen...' : 'Plaatsen'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {connectStatus === 'declined' && (!declinedUntil || declinedUntil.getTime() <= Date.now()) && (
        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
          Deze connectie was eerder niet beschikbaar. Wil je het opnieuw proberen?
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={handleConnect}
              className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              Probeer later opnieuw
            </button>
            <button
              onClick={() => {
                try {
                  const raw = localStorage.getItem('mutedRelationships');
                  const map = raw ? JSON.parse(raw) : {};
                  const targetId = post?.userId || '';
                  if (!targetId) return;
                  const until = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 dagen
                  map[targetId] = until;
                  localStorage.setItem('mutedRelationships', JSON.stringify(map));
                  const ev = new Event('mutedRelationshipsChanged');
                  window.dispatchEvent(ev);
                } catch (e) { console.error('Mute 30d failed:', e); }
              }}
              className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-gray-700"
            >
              Dempen 30 dagen
            </button>
          </div>
        </div>
      )}
      {showConnectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg border shadow p-4 w-full max-w-sm">
            <div className="font-semibold mb-1">Connectieverzoek verstuurd</div>
            <div className="text-sm text-gray-700">
              Bedankt voor je connectieverzoek. Ga ervan uit dat {receiverName} u binnen 48 uur een bevestiging stuurt met een afspraakverzoek.
            </div>
            <div className="mt-3 flex items-center justify-end">
              <button type="button" onClick={() => setShowConnectModal(false)} className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">OK</button>
            </div>
          </div>
        </div>
      )}
      {showIntroductionModal && selectedIntroCandidate && (
        <IntroductionModal
          isOpen={showIntroductionModal}
          onClose={() => setShowIntroductionModal(false)}
          recommendedUserId={selectedIntroCandidate.userId}
          recommendedUserName={selectedIntroCandidate.userName}
          recommendedUserPhoto={selectedIntroCandidate.userPhoto}
        />
      )}

      {/* VibeScore Display - Subtle & Minimal */}
      {vibeScore && (
        <div className="mt-2 pt-2">
          {/* Minimal Header Row */}
          <div className="flex items-center justify-between opacity-60 hover:opacity-100 transition-opacity">
            <div className="flex items-center space-x-1">
              <GoalIcon size={10} weight="regular" className="text-gray-400" />
              <span className="text-[10px] text-gray-400">VibeScore</span>
              <span className="text-[10px] text-gray-400">{vibeScore.score}/100</span>
            </div>
            <button
              onClick={() => setShowVibeScoreDetails(!showVibeScoreDetails)}
              className="text-gray-400 hover:text-gray-600 text-[10px] transition-colors"
              title={showVibeScoreDetails ? "Verberg details" : "Toon details"}
            >
              {showVibeScoreDetails ? '▲' : '▼'}
            </button>
          </div>
          
          {/* Expandable Details - Hidden by Default */}
          {showVibeScoreDetails && (
            <div className="mt-3 space-y-3">
              {/* ChakraBar - Centered */}
              <div className="flex justify-center">
                <ChakraBar 
                  score={vibeScore.score} 
                  breakdown={vibeScore.breakdown}
                  showDetails={false}
                />
              </div>

              {/* Stats Row - Clean Grid */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center space-x-2 text-xs text-purple-700 bg-purple-50 rounded-lg p-2">
                  <WellnessIcon size={13} weight="regular" style={{ color: '#36454F', flexShrink: 0 }} />
                  <span className="truncate">{vibeScore.chakraColor.description}</span>
                </div>
                <div className="flex items-center space-x-2 text-xs text-purple-700 bg-purple-50 rounded-lg p-2">
                  <AnalyticsIcon size={13} weight="regular" style={{ color: '#36454F', flexShrink: 0 }} />
                  <span className="truncate">+{vibeScore.breakdown.wordMatches.positive.length} / -{vibeScore.breakdown.wordMatches.negative.length}</span>
                </div>
              </div>

              {/* Link - Centered */}
              <div className="text-center">
                <button
                  onClick={() => {
                    window.location.hash = '#vibes';
                  }}
                  className="text-xs text-purple-600 hover:text-purple-800 underline"
                >
                  <AnalyticsIcon size={12} weight="regular" className="inline align-[-2px]" style={{ color: '#36454F' }} /> Volledige analyse
                </button>
              </div>
            </div>
          )}
        </div>
      )}


    </div>
  );
};


